import "./Chart.scss";
import range from "lodash/range";
import orderBy from "lodash/orderBy";
import data from "../../data/technologies.json";
import { Link } from "wouter";
import { colors } from "./colors";

import { useEffect, useRef, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
const sortKeys = {
	alphabetical: { fields: ["title"], order: ["asc"] },
	category: { fields: ["category", "title"], order: ["asc", "asc"] },
	grants: {
		fields: [
			"scoreBreakdown.grants",
			"scoreBreakdown.publications",
			"scoreBreakdown.patents",
			"scoreBreakdown.investments",
			"title",
		],
		order: ["desc", "desc", "desc", "desc", "asc"],
	},
	publications: {
		fields: [
			"scoreBreakdown.publications",
			"scoreBreakdown.grants",
			"scoreBreakdown.patents",
			"scoreBreakdown.investments",
			"title",
		],
		order: ["desc", "desc", "desc", "desc", "asc"],
	},
	patents: {
		fields: [
			"scoreBreakdown.patents",
			"scoreBreakdown.grants",
			"scoreBreakdown.publications",
			"scoreBreakdown.investments",
			"title",
		],
		order: ["desc", "desc", "desc", "desc", "asc"],
	},
	investments: {
		fields: [
			"scoreBreakdown.investments",
			"scoreBreakdown.grants",
			"scoreBreakdown.publications",
			"scoreBreakdown.patents",
			"title",
		],
		order: ["desc", "desc", "desc", "desc", "asc"],
	},
};

function calculateBarWidth(score) {
	return score ? 10 * (score - 1) + 8 : 0;
}
function calculateSlugWidth(score) {
	return score ? (8 / calculateBarWidth(score)) * 100 : 0;
}
const sortAnimations = {
	initial: (i) => ({
		opacity: 0.01, //This is set to 0.01 to prevent ios from requiring a second click on all links on the page while content is animating in. I think it checks if anything on the page goes from not visible to visible when you click, and if something did then require a second click, 0.01 counts as visible so the entry animation doesn't trigger this behaviour
		transform: `translateX(${i % 2 === 0 ? "-40" : "40"}px)`,
	}),
	enter: (i) => {
		return {
			opacity: 1,
			transform: "translateX(0px)",
			transition: {
				delay: i * 0.05 + 0.55,
				// when: "beforeChildren",
				type: "tween",
			},
		};
	},
	exit: (i) => ({
		opacity: 0,
		transform: `translateX(${i % 2 === 0 ? "-40" : "40"}px)`,
		transition: {
			delay: i * 0.05,
			// when: "afterChildren",
			type: "tween",
		},
	}),
};

const barAnimations = {
	enter: (i) => ({
		transform: "translateX(0%)",
		opacity: 1,
		visibility: "visible",
		transition: {
			delay: i * 0.05,
			type: "tween",
		},
	}),
	exit: (i) => ({
		transform: "translateX(-100%)",
		opacity: 1,
		transitionEnd: {
			visibility: "hidden",
		},
		transition: {
			delay: i * 0.05,
			type: "tween",
		},
	}),
};

function Bar({ score, category, enabled, index }) {
	return (
		<motion.div
			transition={{ type: "tween" }}
			custom={index}
			variants={barAnimations}
			initial={{
				transform: "translateY(-100%)",
				opacity: 0,
				visibility: "visible",
			}}
			animate={enabled ? "enter" : "exit"}
			// exit={"exit"}
			style={{
				// "visibility": enabled ? "visible" : "hidden",
				// "opacity": enabled ? 1 : 0,
				// "transform": enabled ? "translateX(0)" : "translateX(-100%)",
				"width": `${calculateBarWidth(score)}%`,
				"--slug-width": `${calculateSlugWidth(score)}%`,
				"--slug-color": colors[category].slug,
				"--bar-color": colors[category].bar,
			}}
			className={"score-breakdown-chart__bar"}>
			<span className={"visually-hidden"}>
				{category}: {score}
			</span>
		</motion.div>
	);
}

export function Chart({ filters, sort, controlsHeight }) {
	const sortedData = orderBy(data, sortKeys[sort].fields, sortKeys[sort].order);
	const [contentHeight, setContentHeight] = useState();
	const contentRef = useRef();
	useEffect(() => {
		const cb = () => {
			if (contentRef.current) {
				let rect = contentRef.current.getBoundingClientRect();
				setContentHeight(rect.height);
			}
		};
		cb();
		window.addEventListener("resize", cb);
		return () => {
			window.removeEventListener("resize", cb);
		};
	}, [sortedData]);

	return (
		<div className={"score-breakdown-chart"}>
			<div
				className={"score-breakdown-chart__header"}
				style={{ "--controls-height": controlsHeight + "px" }}>
				<div className={"score-breakdown-chart__header__inner"}>
					<div className={"score-breakdown-chart__column-title"}>
						Technology
					</div>

					{range(0, 10).map((i) => {
						return (
							<div className={"score-breakdown-chart__column-value"} key={i}>
								{i + 1}
							</div>
						);
					})}
				</div>
			</div>
			<div
				className={"score-breakdown-chart__content"}
				style={{ height: contentHeight || null }}>
				<AnimatePresence>
					<motion.div
						className={"score-breakdown-chart__container"}
						key={sort}
						ref={contentRef}
						style={{ position: "absolute", top: 0 }}>
						{sortedData.map((data, i) => {
							let scoreBreakdown = data.scoreBreakdown;
							return (
								<motion.div
									className={"score-breakdown-chart__tech"}
									key={`${data.slug}-${sort}-${i}`}
									custom={i}
									variants={sortAnimations}
									initial={"initial"}
									animate={"enter"}
									exit={"exit"}
									transition={{ type: "tween" }}>
									<div className={"score-breakdown-chart__tech-name"}>
										<Link href={`/score-breakdown/${data.slug}`}>
											{data.title}
										</Link>
									</div>
									<div className={"score-breakdown-chart__data"}>
										{range(0, 10).map((i) => {
											return (
												<div
													className={"score-breakdown-chart__line"}
													style={{ left: `${i * 10}%` }}
													key={i}
												/>
											);
										})}
										<Bar
											score={scoreBreakdown.grants}
											category={"Grants"}
											enabled={filters.grants.value}
											index={i}
										/>
										<Bar
											score={scoreBreakdown.publications}
											category={"Publications"}
											enabled={filters.publications.value}
											index={i}
										/>
										<Bar
											score={scoreBreakdown.patents}
											category={"Patents"}
											enabled={filters.patents.value}
											index={i}
										/>
										<Bar
											score={scoreBreakdown.investments}
											category={"Investments"}
											enabled={filters.investments.value}
											index={i}
										/>
									</div>
								</motion.div>
							);
						})}
					</motion.div>
				</AnimatePresence>
			</div>
		</div>
	);
}
